import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Grid,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import ReusableDialog from "../../../../common/dialog/dialog";
import { useSelector } from "react-redux";
import Loading from "../../../../common/loader/loading";

const FridgeTemperature = ({ onClose, selectedTask, setOpenDefaultSnackbar }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fridge_temp, setFridgeTemp] = useState("");
  const [freezer_temp, setFreezerTemp] = useState("");
  const [comments, setComments] = useState("");
  const [records, setRecords] = useState([]);
  const today = new Date();
  const todayDate = today.toISOString().split("T")[0];
  const [loading, setLoading] = useState(true);
  const [taskId, setTaskId] = useState(selectedTask?.id);

  useEffect(() => {
    if (!taskId && selectedTask?.id) {
      setTaskId(selectedTask.id);
    }
  }, [selectedTask?.id]); // Run only if `selectedTask.id` is not set

  const getCurrentTime24Hour = () => {
    const now = new Date();
    return now.toLocaleTimeString("en-GB"); // e.g., "14:30:45"
  };

  const updateSubTask = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is undefined. Cannot update.");
      return;
    }

    try {
      await axios.put(
        `/api/assessments/updateTask/${taskId}`,
        {
          status: "completed",
          checklist: [],
          updated_by: user?.username || "Unknown",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": user?.roleid || "",
          },
        }
      );
      setOpenDefaultSnackbar(true);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  // Fetch all records from the API
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/fridges/temperatureAll");

      // Get today's date in YYYY-MM-DD format
      const todayDate = new Date().toLocaleDateString("en-CA");

      // Filter records where the date matches today’s date
      const filteredRecords = (response.data.temperatures || []).filter((record) => {
        if (!record.date) return false; // Skip if date is missing
        return new Date(record.date).toLocaleDateString("en-CA") === todayDate;
      });

      setRecords(filteredRecords);
    } catch (error) {
      console.error("Failed to fetch records:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(); // Fetch records when the component mounts
  }, []);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFridgeTemp("");
    setFreezerTemp("");
    setComments("");
  };

  const handleAddRecord = async () => {
    const newRecord = {
      fridge_temp: parseInt(fridge_temp, 10),
      freezer_temp: parseInt(freezer_temp, 10),
      comments,
      date: todayDate,
      time: getCurrentTime24Hour(),
      completed_by: user.username,
    };

    try {
      await axios.post("/api/fridges/temperature", newRecord);
      fetchRecords(); // Refresh the table with the updated data
      handleDialogClose();
    } catch (error) {
      console.error("Failed to add record:", error);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 4 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Tasks'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 2 }}>
        Daily Fridge / Freezer Temperature
      </Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575", mb: 1 }}>
        Date: {todayDate}
      </Typography>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Time
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Fridge Temperature
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Freezer Temperature
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Name
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((record, index) => (
            <TableRow key={index}>
              <TableCell>{record.time}</TableCell>
              <TableCell>{record.fridge_temp} C</TableCell>
              <TableCell>{record.freezer_temp} C</TableCell>
              <TableCell>{record.completed_by}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ mt: 2, mb: 4 }}>
        <Button onClick={handleDialogOpen} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddIcon fontSize='small' /> Add another record
        </Button>
      </Box>

      <ReusableDialog
        isOpen={dialogOpen}
        onClose={handleDialogClose}
        title='Add A Fridge/Freezer Temperature Record'
        onSubmit={handleAddRecord}
        submitText='Submit'
        cancelText='Close'
        disable={!fridge_temp || !freezer_temp || !comments}
      >
        <Typography sx={{ fontSize: "14px", color: "black", mb: 2 }}>
          The ideal fridge temperature is between 0.5°C - 5°C
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography sx={{ fontSize: "14px" }}>Fridge Temperature</Typography>
            <TextField
              value={fridge_temp}
              onChange={(e) => setFridgeTemp(e.target.value)}
              size='small'
              type='number'
              inputProps={{
                min: -100,
                max: 100,
              }}
              fullWidth
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography sx={{ fontSize: "14px" }}>Freezer Temperature</Typography>
            <TextField
              value={freezer_temp}
              onChange={(e) => setFreezerTemp(e.target.value)}
              size='small'
              type='number'
              inputProps={{
                min: -100,
                max: 100,
              }}
              fullWidth
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        <Typography sx={{ fontSize: "14px" }}>Comments</Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          variant='outlined'
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          sx={{ mb: 4 }}
        />
      </ReusableDialog>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
        <Button variant='outlined' onClick={onClose}>
          Previous
        </Button>
        <Button
          variant='contained'
          onClick={() => {
            onClose();
            updateSubTask(taskId);
          }}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default FridgeTemperature;
